import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Handshake, Pencil, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetMyBuyers,
  useGetMySellers,
  useUpdateMyBuyerCode,
  useUpdateMySellerCode,
} from '~/api/organizations';
import { Table, type TableColumn, type TableRow } from '~/components/ui/table';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import type { TradingPartner } from '~/graphql/graphql';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';

type Variant = 'sellers' | 'buyers';
type UpdateCode = (
  organizationId: string,
  code: string,
  onSuccess: () => void
) => void;

// Matches OrganizationService.MAX_CODE_LENGTH on the backend.
const MAX_CODE_LENGTH = 127;

export function TradingPartners({ variant }: { variant: Variant }) {
  return variant === 'sellers' ? <SellersTable /> : <BuyersTable />;
}

// Shared toast wiring for both mutations below — only the mutate function and the variables
// shape (sellerOrganizationId vs. buyerOrganizationId) differ per variant. onSuccess (not
// onSettled) closes the editor: on error the cell must stay open with the typed value intact
// so the user can correct and retry instead of losing their input.
function codeUpdateOptions(t: (key: string) => string, onSuccess: () => void) {
  return {
    onSuccess: () => {
      toast.success(t('settings.trading_partners.update_code_success'));
      onSuccess();
    },
    onError: (error: ClientError) =>
      toast.error(extractGraphQLErrorMessages(error).join(', ')),
  };
}

function SellersTable() {
  const { t } = useTranslation('settings');
  const { data, isLoading } = useGetMySellers();
  const { mutate: updateSellerCode, isPending } = useUpdateMySellerCode();

  const handleUpdateCode: UpdateCode = (organizationId, code, onSuccess) =>
    updateSellerCode(
      { sellerOrganizationId: organizationId, code },
      codeUpdateOptions(t, onSuccess)
    );

  return (
    <TradingPartnerTable
      variant="sellers"
      partners={data?.mySellers ?? []}
      isLoading={isLoading}
      isSaving={isPending}
      onUpdateCode={handleUpdateCode}
    />
  );
}

function BuyersTable() {
  const { t } = useTranslation('settings');
  const { data, isLoading } = useGetMyBuyers();
  const { mutate: updateBuyerCode, isPending } = useUpdateMyBuyerCode();

  const handleUpdateCode: UpdateCode = (organizationId, code, onSuccess) =>
    updateBuyerCode(
      { buyerOrganizationId: organizationId, code },
      codeUpdateOptions(t, onSuccess)
    );

  return (
    <TradingPartnerTable
      variant="buyers"
      partners={data?.myBuyers ?? []}
      isLoading={isLoading}
      isSaving={isPending}
      onUpdateCode={handleUpdateCode}
    />
  );
}

function TradingPartnerTable({
  variant,
  partners,
  isLoading,
  isSaving,
  onUpdateCode,
}: {
  variant: Variant;
  partners: TradingPartner[];
  isLoading: boolean;
  isSaving: boolean;
  onUpdateCode: UpdateCode;
}) {
  const { t } = useTranslation('settings');
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);

  const columns: TableColumn[] = [
    {
      key: 'organizationName',
      header: t('settings.trading_partners.table.organization'),
      width: 20,
    },
    {
      key: 'code',
      header: t('settings.trading_partners.table.code'),
      width: 12,
    },
  ];

  const rows: TableRow[] = partners.map((partner) => {
    const code =
      variant === 'sellers'
        ? partner.sellingOrganizationCode
        : partner.buyingOrganizationCode;

    return {
      id: { value: partner.organizationId },
      organizationName: { value: partner.organizationName },
      code: {
        value: (
          <InlineCodeCell
            code={code}
            isEditing={editingOrgId === partner.organizationId}
            isSaving={isSaving}
            onStartEdit={() => setEditingOrgId(partner.organizationId)}
            onCancel={() => setEditingOrgId(null)}
            onSave={(value) =>
              onUpdateCode(partner.organizationId, value, () =>
                setEditingOrgId(null)
              )
            }
          />
        ),
      },
    };
  });

  const title = t(`settings.trading_partners.${variant}_title`);

  return (
    <div className="w-full bg-neut-bg-alt rounded-lg border border-neut-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-prim-100 rounded-lg">
          <Handshake size={20} className="text-prim" />
        </div>
        <h2 className="text-neut-prim font-semibold">{title}</h2>
      </div>

      {isLoading ? (
        <PageLoadingSpinner name={title} />
      ) : (
        <Table
          minWidth={44}
          rows={rows}
          columns={columns}
          emptyMessage={t(`settings.trading_partners.no_${variant}`)}
          name={`trading-partners-${variant}`}
        />
      )}
    </div>
  );
}

function InlineCodeCell({
  code,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
}: {
  code: string | null | undefined;
  isEditing: boolean;
  isSaving: boolean;
  onStartEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('settings');

  if (isEditing) {
    // A dedicated component (rather than a branch here) so it mounts fresh each time
    // editing starts, seeding its input state straight from `code` with no effect needed.
    return (
      <CodeEditForm
        code={code}
        isSaving={isSaving}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  return (
    <button
      data-testid="code-cell-edit-button"
      type="button"
      onClick={onStartEdit}
      aria-label={t('settings.trading_partners.code_edit_label_with_value', {
        code: code ?? '—',
      })}
      className="group flex items-center gap-2 text-left"
    >
      <span>{code ?? '—'}</span>
      <Pencil
        aria-hidden="true"
        size={12}
        className="opacity-0 group-hover:opacity-100 text-neut-400 shrink-0"
      />
    </button>
  );
}

function CodeEditForm({
  code,
  isSaving,
  onSave,
  onCancel,
}: {
  code: string | null | undefined;
  isSaving: boolean;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('settings');
  const [value, setValue] = useState(code ?? '');

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(value);
      }}
    >
      <input
        data-testid="code-cell-input"
        autoFocus
        disabled={isSaving}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
        aria-label={t('settings.trading_partners.code_input_label')}
        maxLength={MAX_CODE_LENGTH}
        className="w-24 px-2 py-1 text-sm border border-neut-300 rounded focus:outline-none focus:ring-2 focus:ring-prim focus:border-transparent"
      />
      <button
        data-testid="code-cell-save"
        type="submit"
        disabled={isSaving}
        title={t('settings.trading_partners.code_save')}
        aria-label={t('settings.trading_partners.code_save')}
        className="p-1 text-success-600 hover:bg-success-100 rounded disabled:opacity-50"
      >
        <Check aria-hidden="true" size={14} />
      </button>
      <button
        data-testid="code-cell-cancel"
        type="button"
        disabled={isSaving}
        title={t('settings.trading_partners.code_cancel')}
        aria-label={t('settings.trading_partners.code_cancel')}
        onClick={onCancel}
        className="p-1 text-neut-500 hover:bg-neut-100 rounded disabled:opacity-50"
      >
        <X aria-hidden="true" size={14} />
      </button>
    </form>
  );
}
